import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CloudDownload,
  ExternalLink,
  LockKeyhole,
  MapPinned,
  Octagon,
  Pin,
  Search,
  Sparkles,
  Timer,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import type { CompetitionDay, Jump, JumpGroup, Level } from '@wht-xc-utils/shared/domain';
import { sortJudgingPosts } from '@wht-xc-utils/shared/scheduling';

import { EventTabs } from '../components/EventTabs';
import { JudgingPostMap } from '../components/JudgingPostMap';
import { JumpIndicators } from '../components/JumpIndicators';
import { api } from '../lib/api';
import { deriveJumpTransitions, type JumpTransitionTarget } from '../lib/jumpTransitions';

interface DiscoveredEventCourse {
  name: string;
  courseId: string;
  courseUrl: string;
  startDate: string | null;
  canonicalSlug: string | null;
  suggestedLevelId: string | null;
  warning: string | null;
}

interface CourseEventDiscovery {
  eventTitle: string;
  eventSlug: string;
  eventUrl: string;
  courses: DiscoveredEventCourse[];
  warnings: string[];
}

interface CourseEventImportResult {
  courseId: string;
  levelId: string;
  levelDisplayName: string;
  ok: boolean;
  jumpsCreated: number;
  jumpsUpdated: number;
  jumpsRemoved: number;
  jumpsParsed: number;
  removedJumpNumbers: string[];
  warnings: string[];
  error: string | null;
}

interface CourseEventImportSummary {
  coursesImported: number;
  coursesFailed: number;
  jumpsCreated: number;
  jumpsUpdated: number;
  jumpsRemoved: number;
  results: CourseEventImportResult[];
}

export function JumpsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const qc = useQueryClient();

  const levels = useQuery({
    queryKey: ['levels', eventId],
    queryFn: () => api.get<{ levels: Level[] }>(`/api/events/${eventId}/levels`),
    enabled: !!eventId,
  });
  const days = useQuery({
    queryKey: ['days', eventId],
    queryFn: () => api.get<{ days: CompetitionDay[] }>(`/api/events/${eventId}/days`),
    enabled: !!eventId,
  });
  const jumps = useQuery({
    queryKey: ['jumps', eventId],
    queryFn: () => api.get<{ jumps: Jump[] }>(`/api/events/${eventId}/jumps`),
    enabled: !!eventId,
  });
  const groups = useQuery({
    queryKey: ['jumpGroups', eventId],
    queryFn: () => api.get<{ jumpGroups: JumpGroup[] }>(`/api/events/${eventId}/jump-groups`),
    enabled: !!eventId,
  });

  const [courseIds, setCourseIds] = useState<Record<string, string>>({});
  const [pendingLevel, setPendingLevel] = useState<string | null>(null);
  const [eventCourseUrl, setEventCourseUrl] = useState('');
  const [eventCourseMappings, setEventCourseMappings] = useState<Record<string, string>>({});

  const fetchCourse = useMutation({
    mutationFn: (input: { levelId: string; courseId: string }) =>
      api.post<{
        summary: {
          jumpsCreated: number;
          jumpsUpdated: number;
          jumpsRemoved: number;
          jumpsParsed: number;
          featureCount: number;
          warnings: string[];
          removedJumpNumbers: string[];
        };
      }>(`/api/events/${eventId}/levels/${input.levelId}/fetch-course`, {
        courseId: input.courseId,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['jumps', eventId] });
      qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] });
      qc.invalidateQueries({ queryKey: ['assignments', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
      setPendingLevel(vars.levelId);
    },
  });

  const importEventCourses = useMutation({
    mutationFn: (mappings: Array<{ courseId: string; levelId: string }>) =>
      api.post<{ summary: CourseEventImportSummary }>(
        `/api/events/${eventId}/course-imports/apply-event`,
        { mappings },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jumps', eventId] });
      qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] });
      qc.invalidateQueries({ queryKey: ['assignments', eventId] });
      qc.invalidateQueries({ queryKey: ['levels', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const discoverEventCourses = useMutation({
    mutationFn: (eventUrl: string) =>
      api.post<{ discovery: CourseEventDiscovery }>(
        `/api/events/${eventId}/course-imports/discover-event`,
        { eventUrl },
      ),
    onMutate: () => {
      importEventCourses.reset();
    },
    onSuccess: ({ discovery }) => {
      setEventCourseMappings(
        Object.fromEntries(
          discovery.courses.map((course) => [course.courseId, course.suggestedLevelId ?? '']),
        ),
      );
    },
  });

  const optimize = useMutation({
    mutationFn: (competitionDayId?: string) =>
      api.post<{
        summary: {
          groupsCreated: number;
          groupsPinned: number;
          byDay: Array<{ competitionDayId: string; groupsCreated: number; groupsPinned: number }>;
        };
      }>(`/api/events/${eventId}/jump-groups/optimize`, { competitionDayId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] }),
  });

  const togglePin = useMutation({
    mutationFn: (input: { id: string; pinned: boolean }) =>
      api.patch<{ jumpGroup: JumpGroup }>(`/api/events/${eventId}/jump-groups/${input.id}`, {
        pinned: input.pinned,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] }),
  });

  const deleteGroup = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${eventId}/jump-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] }),
  });

  const updateJump = useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      difficultToJudge?: boolean;
      stoppingPoint?: boolean;
      timingPoint?: boolean;
    }) => api.patch<{ jump: Jump }>(`/api/events/${eventId}/jumps/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jumps', eventId] }),
  });

  const createGroup = useMutation({
    mutationFn: (input: { competitionDayId: string; jumpIds: string[] }) =>
      api.post<{ jumpGroup: JumpGroup }>(`/api/events/${eventId}/jump-groups`, {
        competitionDayId: input.competitionDayId,
        jumpIds: input.jumpIds,
        pinned: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] });
      setManualSelectedJumps(new Set());
    },
  });

  const updateGroupJumps = useMutation({
    mutationFn: (input: { id: string; jumpIds: string[] }) =>
      api.patch<{ jumpGroup: JumpGroup }>(`/api/events/${eventId}/jump-groups/${input.id}`, {
        jumpIds: input.jumpIds,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jumpGroups', eventId] }),
  });

  const jumpsByLevel = useMemo(() => {
    const out = new Map<string, Jump[]>();
    for (const j of jumps.data?.jumps ?? []) {
      const list = out.get(j.levelId) ?? [];
      list.push(j);
      out.set(j.levelId, list);
    }
    return out;
  }, [jumps.data]);
  const jumpById = useMemo(
    () => new Map((jumps.data?.jumps ?? []).map((j) => [j.id, j])),
    [jumps.data],
  );
  const levelById = useMemo(
    () => new Map((levels.data?.levels ?? []).map((l) => [l.id, l])),
    [levels.data],
  );
  const jumpTransitions = useMemo(
    () =>
      deriveJumpTransitions(
        jumps.data?.jumps ?? [],
        groups.data?.jumpGroups ?? [],
        levels.data?.levels ?? [],
      ),
    [groups.data, jumps.data, levels.data],
  );
  const reviewedEventCourseMappings = useMemo(
    () =>
      (discoverEventCourses.data?.discovery.courses ?? [])
        .map((course) => ({
          courseId: course.courseId,
          levelId: eventCourseMappings[course.courseId] ?? '',
        }))
        .filter((mapping) => !!mapping.levelId),
    [discoverEventCourses.data, eventCourseMappings],
  );
  const eventCourseMappingsHaveDuplicates =
    new Set(reviewedEventCourseMappings.map((mapping) => mapping.levelId)).size !==
    reviewedEventCourseMappings.length;

  const sortedDays = useMemo(
    () => (days.data?.days ?? []).slice().sort((a, b) => a.dayOrder - b.dayOrder),
    [days.data],
  );
  const [mapDayId, setMapDayId] = useState('');
  const effectiveMapDayId = mapDayId || sortedDays[0]?.id || '';
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const groupsForDay = (dayId: string) =>
    sortJudgingPosts(
      (groups.data?.jumpGroups ?? []).filter((group) => group.competitionDayId === dayId),
      jumps.data?.jumps ?? [],
      levels.data?.levels ?? [],
    );

  const mapDayGroups = useMemo(
    () =>
      sortJudgingPosts(
        (groups.data?.jumpGroups ?? []).filter(
          (group) => group.competitionDayId === effectiveMapDayId,
        ),
        jumps.data?.jumps ?? [],
        levels.data?.levels ?? [],
      ),
    [effectiveMapDayId, groups.data, jumps.data, levels.data],
  );

  useEffect(() => {
    if (!mapDayGroups.some((group) => group.id === selectedGroupId)) {
      setSelectedGroupId(mapDayGroups[0]?.id ?? '');
    }
  }, [mapDayGroups, selectedGroupId]);

  const selectedGroup = mapDayGroups.find((group) => group.id === selectedGroupId) ?? null;
  const selectedMapDay = sortedDays.find((day) => day.id === effectiveMapDayId);

  // Day-scoped manual-group form ("Create / extend group").
  const [manualDayId, setManualDayId] = useState<string>('');
  const effectiveManualDayId = manualDayId || sortedDays[0]?.id || '';
  const [manualSelectedJumps, setManualSelectedJumps] = useState<Set<string>>(new Set());
  const [manualTargetGroup, setManualTargetGroup] = useState<string>('');

  const dayLevels = useMemo(() => {
    if (!effectiveManualDayId) return [] as Level[];
    return (levels.data?.levels ?? [])
      .filter((l) => l.competitionDayId === effectiveManualDayId)
      .slice()
      .sort((a, b) => (a.runOrder ?? 9999) - (b.runOrder ?? 9999));
  }, [levels.data, effectiveManualDayId]);

  const manualValid = useMemo(() => {
    if (!effectiveManualDayId) return false;
    if (manualSelectedJumps.size < 2 && !manualTargetGroup) return false;
    if (manualSelectedJumps.size < 1) return false;
    const selectedLevels = new Set<string>();
    for (const id of manualSelectedJumps) {
      const j = jumpById.get(id);
      if (!j) return false;
      const lvl = levelById.get(j.levelId);
      if (!lvl || lvl.competitionDayId !== effectiveManualDayId) return false;
      if (selectedLevels.has(j.levelId)) return false;
      selectedLevels.add(j.levelId);
    }
    return true;
  }, [manualSelectedJumps, manualTargetGroup, effectiveManualDayId, jumpById, levelById]);

  const onSubmitManual = () => {
    if (!manualValid || !effectiveManualDayId) return;
    if (manualTargetGroup) {
      const target = (groups.data?.jumpGroups ?? []).find((g) => g.id === manualTargetGroup);
      if (!target) return;
      const merged = new Set<string>(target.jumpIds);
      for (const id of manualSelectedJumps) merged.add(id);
      updateGroupJumps.mutate({ id: manualTargetGroup, jumpIds: [...merged] });
    } else {
      createGroup.mutate({
        competitionDayId: effectiveManualDayId,
        jumpIds: [...manualSelectedJumps],
      });
    }
  };

  const renderGroupLabel = (g: JumpGroup): JSX.Element => {
    const parts = g.jumpIds
      .map((id) => {
        const jump = jumpById.get(id);
        const level = jump ? levelById.get(jump.levelId) : null;
        return { id, jump, level };
      })
      .sort(
        (a, b) =>
          (a.level?.runOrder ?? 9999) - (b.level?.runOrder ?? 9999) ||
          (a.jump?.displayOrder ?? 9999) - (b.jump?.displayOrder ?? 9999),
      )
      .map(({ id, jump, level }) => (
        <span key={id}>
          <strong>{level?.displayName ?? '?'}</strong>: {jump?.number ?? '?'}
          {jump?.name ? ` (${jump.name})` : ''}
          {jump ? <JumpIndicators jump={jump} /> : null}
        </span>
      ));
    return (
      <>
        {parts.map((p, i) => (
          <span key={i}>
            {p}
            {i < parts.length - 1 ? <span className="muted"> · </span> : null}
          </span>
        ))}
      </>
    );
  };

  return (
    <>
      <EventTabs />

      <div className="card">
        <div className="card-heading-row">
          <div>
            <span className="eyebrow">Course discovery</span>
            <h2>Import a CrossCountry App event</h2>
            <p className="muted">
              Paste the public event page once. Courses are discovered by name and suggested against
              this event's planner levels before anything is imported.
            </p>
          </div>
          <CloudDownload aria-hidden="true" />
        </div>

        <div className="toolbar">
          <input
            className="grow"
            type="text"
            placeholder="https://www.crosscountryapp.com/events/event-id"
            value={eventCourseUrl}
            onChange={(event) => setEventCourseUrl(event.target.value)}
          />
          <button
            className="primary"
            disabled={!eventCourseUrl.trim() || discoverEventCourses.isPending}
            onClick={() => discoverEventCourses.mutate(eventCourseUrl.trim())}
          >
            <Search aria-hidden="true" />
            {discoverEventCourses.isPending ? 'Discovering…' : 'Discover courses'}
          </button>
        </div>

        {discoverEventCourses.error ? (
          <div className="notice error">{(discoverEventCourses.error as Error).message}</div>
        ) : null}

        {discoverEventCourses.data ? (
          <>
            <div className="notice info">
              Found {discoverEventCourses.data.discovery.courses.length} public course
              {discoverEventCourses.data.discovery.courses.length === 1 ? '' : 's'} for{' '}
              <strong>{discoverEventCourses.data.discovery.eventTitle}</strong>. Review the
              suggested planner levels before fetching geometry.
            </div>
            {discoverEventCourses.data.discovery.warnings.map((warning) => (
              <div key={warning} className="notice">
                {warning}
              </div>
            ))}

            <table className="aligned-table">
              <colgroup>
                <col style={{ width: '28%' }} />
                <col style={{ width: '24%' }} />
                <col style={{ width: '32%' }} />
                <col style={{ width: '16%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Published course</th>
                  <th>Course ID</th>
                  <th>Planner level</th>
                  <th>Match</th>
                </tr>
              </thead>
              <tbody>
                {discoverEventCourses.data.discovery.courses.map((course) => (
                  <tr key={course.courseId}>
                    <td>
                      <div>
                        <a
                          className="course-source-link"
                          href={course.courseUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <strong>{course.name}</strong>
                          <ExternalLink aria-hidden="true" />
                        </a>
                        {course.startDate ? (
                          <div className="muted">
                            Date {new Date(course.startDate).toLocaleDateString()}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <code>{course.courseId}</code>
                    </td>
                    <td>
                      <select
                        value={eventCourseMappings[course.courseId] ?? ''}
                        onChange={(event) =>
                          setEventCourseMappings((current) => ({
                            ...current,
                            [course.courseId]: event.target.value,
                          }))
                        }
                      >
                        <option value="">— do not import —</option>
                        {(levels.data?.levels ?? []).map((level) => (
                          <option key={level.id} value={level.id}>
                            {level.displayName}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {eventCourseMappings[course.courseId] ? (
                        <span className="pill success">
                          {eventCourseMappings[course.courseId] === course.suggestedLevelId
                            ? 'matched'
                            : 'selected'}
                        </span>
                      ) : course.warning ? (
                        <span className="muted">{course.warning}</span>
                      ) : (
                        <span className="pill">not selected</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {eventCourseMappingsHaveDuplicates ? (
              <div className="notice error">
                Each planner level can receive only one course in this batch.
              </div>
            ) : null}

            <div className="workflow-actions">
              <button
                className="primary"
                disabled={
                  reviewedEventCourseMappings.length === 0 ||
                  eventCourseMappingsHaveDuplicates ||
                  importEventCourses.isPending
                }
                onClick={() => importEventCourses.mutate(reviewedEventCourseMappings)}
              >
                <CloudDownload aria-hidden="true" />
                {importEventCourses.isPending
                  ? 'Fetching course geometry…'
                  : `Fetch ${reviewedEventCourseMappings.length} mapped course${
                      reviewedEventCourseMappings.length === 1 ? '' : 's'
                    }`}
              </button>
            </div>

            {importEventCourses.error ? (
              <div className="notice error">{(importEventCourses.error as Error).message}</div>
            ) : null}

            {importEventCourses.data ? (
              <div
                className={
                  importEventCourses.data.summary.coursesFailed > 0 ? 'notice' : 'notice info'
                }
              >
                Imported {importEventCourses.data.summary.coursesImported} course
                {importEventCourses.data.summary.coursesImported === 1 ? '' : 's'} with{' '}
                {importEventCourses.data.summary.jumpsCreated} new and{' '}
                {importEventCourses.data.summary.jumpsUpdated} updated
                {importEventCourses.data.summary.jumpsRemoved > 0
                  ? `, and ${importEventCourses.data.summary.jumpsRemoved} removed`
                  : ''}{' '}
                jumps.
                {importEventCourses.data.summary.coursesFailed > 0 ? (
                  <div>
                    {importEventCourses.data.summary.results
                      .filter((result) => !result.ok)
                      .map((result) => (
                        <div key={result.courseId}>
                          <strong>{result.levelDisplayName}:</strong> {result.error}
                        </div>
                      ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="card">
        <div className="card-heading-row">
          <div>
            <span className="eyebrow">Course operations</span>
            <h2>Judging posts</h2>
            <p className="muted">
              Review each post spatially, including the order a judge moves between divisions.
              Optimize rebuilds unpinned posts while preserving curated pins.
            </p>
          </div>
          <MapPinned aria-hidden="true" />
        </div>

        <div className="toolbar">
          <button
            className="primary"
            disabled={optimize.isPending}
            onClick={() => optimize.mutate(undefined)}
          >
            <Sparkles aria-hidden="true" />
            {optimize.isPending ? 'Optimizing…' : 'Optimize all days'}
          </button>
          {effectiveMapDayId ? (
            <button
              disabled={optimize.isPending}
              onClick={() => optimize.mutate(effectiveMapDayId)}
            >
              Optimize {selectedMapDay?.label || selectedMapDay?.date || 'selected day'}
            </button>
          ) : null}
          {optimize.data ? (
            <span className="success">
              {optimize.data.summary.groupsCreated} post(s) suggested,{' '}
              {optimize.data.summary.groupsPinned} pinned.
            </span>
          ) : null}
        </div>

        {sortedDays.length === 0 ? (
          <p className="muted">No competition days yet. Add at least one on the Schedule tab.</p>
        ) : (
          <>
            <div className="day-switcher" aria-label="Competition day">
              {sortedDays.map((day) => (
                <button
                  key={day.id}
                  className={day.id === effectiveMapDayId ? 'active' : ''}
                  onClick={() => {
                    setMapDayId(day.id);
                    setSelectedGroupId('');
                  }}
                >
                  {day.label || day.date}
                </button>
              ))}
            </div>

            <div className="post-workspace">
              <aside className="post-list-panel">
                <div className="post-list-header">
                  <strong>{selectedMapDay?.label || selectedMapDay?.date}</strong>
                  <span>
                    {mapDayGroups.length} judging post
                    {mapDayGroups.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="post-list">
                  {mapDayGroups.length === 0 ? (
                    <div className="post-list-empty">
                      No posts yet for this day. Optimize or create one manually below.
                    </div>
                  ) : (
                    mapDayGroups.map((group, index) => (
                      <div
                        key={group.id}
                        className={`post-list-item${group.id === selectedGroupId ? ' active' : ''}`}
                      >
                        <button
                          className="post-list-select"
                          onClick={() => setSelectedGroupId(group.id)}
                        >
                          <span className="post-list-title">
                            <strong>Post {String(index + 1).padStart(2, '0')}</strong>
                            {group.pinned ? (
                              <span className="pill success">pinned</span>
                            ) : (
                              <span className="pill">auto</span>
                            )}
                          </span>
                          <span className="post-list-jumps">{renderGroupLabel(group)}</span>
                          <span className="post-list-meta">
                            <span className="pill">
                              {group.distanceM != null
                                ? `${Math.round(group.distanceM)} m span`
                                : 'Distance unavailable'}
                            </span>
                            <span className="pill">
                              {group.jumpIds.length} division
                              {group.jumpIds.length === 1 ? '' : 's'}
                            </span>
                          </span>
                        </button>
                        <span className="post-list-actions">
                          <button
                            className="subtle"
                            aria-label={group.pinned ? 'Unpin post' : 'Pin post'}
                            title={group.pinned ? 'Unpin post' : 'Pin post'}
                            onClick={() =>
                              togglePin.mutate({
                                id: group.id,
                                pinned: !group.pinned,
                              })
                            }
                          >
                            {group.pinned ? (
                              <LockKeyhole aria-hidden="true" />
                            ) : (
                              <Pin aria-hidden="true" />
                            )}
                          </button>
                          <button
                            className="subtle danger"
                            aria-label="Delete post"
                            title="Delete post"
                            onClick={() => {
                              if (confirm('Delete post?')) {
                                deleteGroup.mutate(group.id);
                              }
                            }}
                          >
                            <Trash2 aria-hidden="true" />
                          </button>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </aside>

              <section className="post-map-panel">
                <div className="post-map-header">
                  <strong>
                    {selectedGroup
                      ? `Post ${String(
                          mapDayGroups.findIndex((group) => group.id === selectedGroup.id) + 1,
                        ).padStart(2, '0')}`
                      : 'Post map'}
                  </strong>
                  <span>
                    The full show area stays in view while numbered markers highlight the selected
                    post.
                  </span>
                </div>
                <JudgingPostMap
                  group={selectedGroup}
                  jumps={jumps.data?.jumps ?? []}
                  levels={levels.data?.levels ?? []}
                  contextJumps={jumps.data?.jumps ?? []}
                />
              </section>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h2>Create or extend a judging post</h2>
        <p className="muted">
          Pick a competition day, then check a jump from each of 2+ different levels on that day.
          Creates a pinned post (or extends an existing one), so the optimizer keeps it across
          re-runs.
        </p>
        <div className="toolbar" style={{ flexWrap: 'wrap' }}>
          <label>
            Day:&nbsp;
            <select
              value={effectiveManualDayId}
              onChange={(e) => {
                setManualDayId(e.target.value);
                setManualSelectedJumps(new Set());
                setManualTargetGroup('');
              }}
            >
              {sortedDays.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label || d.date}
                </option>
              ))}
            </select>
          </label>
          <label>
            Target:&nbsp;
            <select
              value={manualTargetGroup}
              onChange={(e) => setManualTargetGroup(e.target.value)}
            >
              <option value="">— new post —</option>
              {groupsForDay(effectiveManualDayId).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.jumpIds
                    .map((id) => {
                      const j = jumpById.get(id);
                      const l = j ? levelById.get(j.levelId) : null;
                      return `${l?.displayName ?? '?'} ${j?.number ?? '?'}`;
                    })
                    .join(' + ')}
                  {g.pinned ? ' (pinned)' : ''}
                </option>
              ))}
            </select>
          </label>
          <button
            className="primary"
            disabled={!manualValid || createGroup.isPending || updateGroupJumps.isPending}
            onClick={onSubmitManual}
          >
            {manualTargetGroup ? 'Add jumps to post' : 'Add pinned post'}
          </button>
          {createGroup.error ? (
            <span className="error">{(createGroup.error as Error).message}</span>
          ) : null}
        </div>
        {dayLevels.length === 0 ? (
          <p className="muted">No levels on this day yet.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            {dayLevels.map((l) => (
              <div key={l.id}>
                <strong>{l.displayName}</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                  {(jumpsByLevel.get(l.id) ?? [])
                    .slice()
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((j) => (
                      <label key={j.id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={manualSelectedJumps.has(j.id)}
                          onChange={(e) => {
                            const next = new Set(manualSelectedJumps);
                            if (e.target.checked) {
                              // Replace any existing selection for the same level.
                              for (const existing of next) {
                                const ej = jumpById.get(existing);
                                if (ej?.levelId === j.levelId) next.delete(existing);
                              }
                              next.add(j.id);
                            } else {
                              next.delete(j.id);
                            }
                            setManualSelectedJumps(next);
                          }}
                        />
                        <span>
                          {j.number}
                          {j.name ? ` (${j.name})` : ''}
                          <JumpIndicators jump={j} />
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(levels.data?.levels ?? []).map((l) => (
        <div key={l.id} className="card">
          <h2>
            {l.displayName}{' '}
            <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>
              ({(jumpsByLevel.get(l.id) ?? []).length} jumps
              {l.courseFetchedAt
                ? ` • fetched ${new Date(l.courseFetchedAt).toLocaleString()}`
                : ''}
              )
            </span>
          </h2>
          <div className="toolbar">
            <input
              type="text"
              placeholder="Course ID or app.crosscountryapp.com URL"
              value={courseIds[l.id] ?? l.courseId ?? ''}
              onChange={(e) => setCourseIds({ ...courseIds, [l.id]: e.target.value })}
              style={{ maxWidth: 520 }}
            />
            <button
              className="primary"
              disabled={
                !(courseIds[l.id] ?? l.courseId) || (fetchCourse.isPending && pendingLevel === l.id)
              }
              onClick={() =>
                fetchCourse.mutate({
                  levelId: l.id,
                  courseId: (courseIds[l.id] ?? l.courseId ?? '').trim(),
                })
              }
            >
              {fetchCourse.isPending && pendingLevel === l.id ? 'Fetching…' : 'Fetch course'}
            </button>
          </div>
          {(jumpsByLevel.get(l.id) ?? []).length === 0 ? (
            <p className="muted">No jumps yet for this level.</p>
          ) : (
            <table className="aligned-table">
              <colgroup>
                <col style={{ width: '13%' }} />
                <col style={{ width: '27%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Name</th>
                  <th>Jump attributes</th>
                  <th>From</th>
                  <th>To</th>
                </tr>
              </thead>
              <tbody>
                {(jumpsByLevel.get(l.id) ?? [])
                  .slice()
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((j) => (
                    <tr className="jump-row" id={`jump-${j.id}`} key={j.id}>
                      <td>
                        <strong>{j.number}</strong>
                      </td>
                      <td>{j.name ?? <span className="muted">—</span>}</td>
                      <td>
                        <div className="jump-attribute-controls">
                          <button
                            type="button"
                            className={j.difficultToJudge ? 'active difficult' : ''}
                            title="Difficult to judge"
                            aria-label={`Difficult to judge: jump ${j.number}`}
                            aria-pressed={j.difficultToJudge}
                            onClick={() =>
                              updateJump.mutate({
                                id: j.id,
                                difficultToJudge: !j.difficultToJudge,
                              })
                            }
                          >
                            <TriangleAlert aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={j.timingPoint ? 'active timing' : ''}
                            title="Timing point"
                            aria-label={`Timing point: jump ${j.number}`}
                            aria-pressed={j.timingPoint}
                            onClick={() =>
                              updateJump.mutate({
                                id: j.id,
                                timingPoint: !j.timingPoint,
                              })
                            }
                          >
                            <Timer aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={j.stoppingPoint ? 'active stopping' : ''}
                            title="Stopping point"
                            aria-label={`Stopping point: jump ${j.number}`}
                            aria-pressed={j.stoppingPoint}
                            onClick={() =>
                              updateJump.mutate({
                                id: j.id,
                                stoppingPoint: !j.stoppingPoint,
                              })
                            }
                          >
                            <Octagon aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      <td>
                        <JumpTransitionLink target={jumpTransitions.get(j.id)?.from ?? null} />
                      </td>
                      <td>
                        <JumpTransitionLink target={jumpTransitions.get(j.id)?.to ?? null} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </>
  );
}

function JumpTransitionLink({ target }: { target: JumpTransitionTarget | null }) {
  if (!target) return <span className="muted">—</span>;
  return (
    <a
      className="jump-transition-link"
      href={`#jump-${encodeURIComponent(target.jumpId)}`}
      title={`Go to jump ${target.label}`}
    >
      {target.label}
    </a>
  );
}
